import React, { useState, useEffect } from "react";
import { Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
import moment from "moment";
import "./ProjectDocumentsTab.css";
import { HiOutlineDocumentText } from "react-icons/hi";
import { fetchDocumentTypesByOrganisation } from "../../Api/ProjectMaster";

const ProjectDocumentsTab = ({
  // Data
  documents,
  documentsLoading,

  // Modal State
  showUploadModal,
  setShowUploadModal,

  // Form State
  documentTitle,
  setDocumentTitle,
  documentType,
  setDocumentType,
  selectedFiles,

  // Loading State
  uploadingDocuments,

  // Handlers
  handleFileSelect,
  handleUploadDocuments,
  handleDownloadDocument,
  handleDeleteDocument,

  // Organization ID (passed from parent)
  orgId,
}) => {
  // State for document types from API
  const [documentTypes, setDocumentTypes] = useState([]);
  const [docTypesLoading, setDocTypesLoading] = useState(false);
  const [docTypesError, setDocTypesError] = useState(null);

  // State for mapping document type GUIDs to names in the table
  const [docTypeNameMap, setDocTypeNameMap] = useState({});

  // Fetch document types when component mounts OR when orgId changes
  useEffect(() => {
    const loadDocumentTypes = async () => {
      if (!orgId) {
        console.log("No orgId provided, skipping document types fetch");
        return;
      }

      setDocTypesLoading(true);
      setDocTypesError(null);
      try {
        const types = await fetchDocumentTypesByOrganisation(orgId);
        console.log("Fetched document types:", types);
        setDocumentTypes(types || []);

        // Create a map for GUID to Name
        const map = {};
        (types || []).forEach((type) => {
          if (type.docTypeGuid && type.docTypeName) {
            map[type.docTypeGuid.toLowerCase()] = type.docTypeName;
          }
        });
        setDocTypeNameMap(map);
        console.log("Document type name map:", map);
      } catch (error) {
        console.error("Error loading document types:", error);
        setDocTypesError("Failed to load document types");
        setDocumentTypes([]);
      } finally {
        setDocTypesLoading(false);
      }
    };

    // Load document types on component mount and when orgId changes
    loadDocumentTypes();
  }, [orgId]);

  // Function to get document type name from GUID
  const getDocumentTypeName = (docTypeGuid) => {
    if (!docTypeGuid) return "General";

    // Check if it's a GUID format (UUID)
    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        docTypeGuid,
      );

    if (isGuid) {
      const lowerGuid = docTypeGuid.toLowerCase();
      const mappedName = docTypeNameMap[lowerGuid];
      if (mappedName) {
        return mappedName;
      }
      // If not found in map, return truncated GUID
      return docTypeGuid.substring(0, 8);
    }
    // If it's not a GUID (legacy data), return as is
    return docTypeGuid;
  };

  // Reset modal form
  const resetModalForm = () => {
    setDocumentTitle("");
    setDocumentType("");
  };

  // Check if form is valid for submission (document type is now optional)
  const isFormValid = () => {
    return selectedFiles.length > 0 && documentTitle.trim() !== "";
  };

  return (
    <div className="pdt-documents-container pdt-fade-in">
      <div className="pdt-document-card">
        <div className="pdt-header">
          <div className="pdt-title-section">
            <h3 className="pdt-main-title">
              <i className="fas fa-folder-open me-2"></i>
              Project Documents
            </h3>
            <p className="pdt-subtitle">
              Manage all documents related to this project
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowUploadModal(true)}
            className="pdt-btn-upload"
          >
            <i className="fas fa-upload"></i>
            Upload Documents
          </Button>
        </div>

        {documentsLoading || docTypesLoading ? (
          <div className="pdt-loading-container">
            <div className="pdt-spinner"></div>
            <p className="pdt-loading-text">
              {documentsLoading
                ? "Loading documents..."
                : "Loading document types..."}
            </p>
          </div>
        ) : documents.length === 0 ? (
          <div className="pdt-empty-container">
            <div className="pdt-empty-icon">
              <i className="fas fa-folder-open"></i>
            </div>
            <h5 className="pdt-empty-title">No Documents Found</h5>
            <p className="pdt-empty-message">
              No documents have been uploaded for this project yet.
            </p>
            <Button
              variant="outline-primary"
              onClick={() => setShowUploadModal(true)}
              className="pdt-btn-outline-upload"
            >
              <i className="fas fa-plus"></i>
              Upload Your First Document
            </Button>
          </div>
        ) : (
          <div className="pdt-table-wrapper">
            <table className="pdt-document-table">
              <thead className="pdt-table-header">
                <tr>
                  <th className="pdt-col-name">Document Name</th>
                  <th className="pdt-col-type">Type</th>
                  <th className="pdt-col-version">Version</th>
                  <th className="pdt-col-uploadedby">Uploaded By</th>
                  <th className="pdt-col-date">Upload Date</th>
                  <th className="pdt-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, index) => (
                  <tr key={doc.documentId || index} className="pdt-table-row">
                    <td className="pdt-col-name">
                      <div className="pdt-document-name">
                        <div className="pdt-document-icon">
                          <HiOutlineDocumentText />
                        </div>
                        <div className="pdt-document-title">
                          {doc.documentTitle}
                        </div>
                      </div>
                    </td>
                    <td className="pdt-col-type">
                      <Badge bg="secondary" className="pdt-type-badge">
                        {getDocumentTypeName(doc.documentType)}
                      </Badge>
                    </td>
                    <td className="pdt-col-version">
                      <div className="pdt-version-badge">
                        {doc.documentVersion || 1}
                      </div>
                    </td>
                    <td className="pdt-col-uploadedby">
                      <div className="pdt-uploadedby-info">
                        <div className="pdt-user-avatar">
                          {(doc.createdBy || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="pdt-uploadedby-name">
                          {doc.createdBy || "Unknown"}
                        </div>
                      </div>
                    </td>
                    <td className="pdt-col-date">
                      <div className="pdt-date-info">
                        <div className="pdt-date-main">
                          {doc.createdOnDate
                            ? moment(doc.createdOnDate).format("MMM DD, YYYY")
                            : "-"}
                        </div>
                      </div>
                    </td>
                    <td className="pdt-col-actions">
                      <div className="pdt-actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                          className="pdt-btn-download"
                          title="Download Document"
                        >
                          Download
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            handleDeleteDocument(
                              doc.documentId,
                              doc.documentTitle,
                            )
                          }
                          className="pdt-btn-delete"
                          title="Delete Document"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      <Modal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          resetModalForm();
        }}
        size="lg"
        centered
        className="pdt-modal"
      >
        <Modal.Header closeButton className="pdt-modal-header">
          <Modal.Title className="pdt-modal-title">
            <i className="fas fa-cloud-upload-alt me-2"></i>
            Upload Documents
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pdt-modal-body">
          <Form>
            <div className="pdt-form-group">
              <Form.Label className="pdt-form-label">
                <i className="fas fa-heading me-1"></i>
                Document Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter document title"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                className="pdt-form-control"
                required
              />
            </div>

            <div className="pdt-form-group">
              <Form.Label className="pdt-form-label">
                <i className="fas fa-tag me-1"></i>
                Document Type <span className="text-muted">(Optional)</span>
              </Form.Label>
              {docTypesLoading ? (
                <div className="pdt-loading-types">
                  <Spinner size="sm" animation="border" />
                  <span className="ms-2">Loading document types...</span>
                </div>
              ) : docTypesError ? (
                <div className="pdt-error-message">
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  {docTypesError}
                </div>
              ) : documentTypes.length > 0 ? (
                <>
                  <Form.Select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="pdt-form-select"
                  >
                    <option value="">-- Select Document Type (Optional) --</option>
                    {documentTypes.map((type) => (
                      <option key={type.id} value={type.docTypeGuid}>
                        {type.docTypeName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="pdt-form-text text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    You can select a document type or leave it empty
                  </Form.Text>
                </>
              ) : (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter document type (optional)"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="pdt-form-control"
                  />
                  <Form.Text className="pdt-form-text text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    You can enter any document type or leave it empty
                  </Form.Text>
                </>
              )}
              {documentTypes.length === 0 &&
                !docTypesLoading &&
                !docTypesError && (
                  <Form.Text className="pdt-form-text text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Document type is optional. You can leave it empty or enter
                    your own.
                  </Form.Text>
                )}
            </div>

            <div className="pdt-form-group">
              <Form.Label className="pdt-form-label">
                <i className="fas fa-paperclip me-1"></i>
                Select Files <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
                className="pdt-file-input"
                required
              />
              <Form.Text className="pdt-form-text">
                <i className="fas fa-info-circle me-1"></i>
                Maximum file size: 25MB per file. Supported formats: PDF, Word,
                Excel, PowerPoint, Images, Text, Archives
              </Form.Text>
            </div>

            {selectedFiles.length > 0 && (
              <div className="pdt-selected-files">
                <div className="pdt-selected-files-title">
                  <i className="fas fa-file-alt me-2"></i>
                  Selected Files ({selectedFiles.length})
                </div>
                <ul className="pdt-file-list">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="pdt-file-item">
                      <div className="pdt-file-info">
                        <span className="pdt-file-name">{file.name}</span>
                      </div>
                      <div className="pdt-file-size">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className="pdt-modal-footer">
          <Button
            variant="secondary"
            onClick={() => {
              setShowUploadModal(false);
              resetModalForm();
            }}
            className="pdt-btn-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadDocuments}
            disabled={!isFormValid() || uploadingDocuments}
            className="pdt-btn-submit"
          >
            {uploadingDocuments ? (
              <>
                <div className="pdt-btn-spinner"></div>
                Uploading...
              </>
            ) : (
              <>
                <i className="fas fa-cloud-upload-alt me-2"></i>
                Upload {selectedFiles.length} File
                {selectedFiles.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectDocumentsTab;

// import React, { useState, useEffect } from "react";
// import { Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
// import moment from "moment";
// import "./ProjectDocumentsTab.css";
// import { HiOutlineDocumentText } from "react-icons/hi";
// import { fetchDocumentTypesByOrganisation } from "../../Api/ProjectMaster";

// const ProjectDocumentsTab = ({
//   // Data
//   documents,
//   documentsLoading,

//   // Modal State
//   showUploadModal,
//   setShowUploadModal,

//   // Form State
//   documentTitle,
//   setDocumentTitle,
//   documentType,
//   setDocumentType,
//   selectedFiles,

//   // Loading State
//   uploadingDocuments,

//   // Handlers
//   handleFileSelect,
//   handleUploadDocuments,
//   handleDownloadDocument,
//   handleDeleteDocument,

//   // Organization ID (passed from parent)
//   orgId,
// }) => {
//   // State for document types from API
//   const [documentTypes, setDocumentTypes] = useState([]);
//   const [docTypesLoading, setDocTypesLoading] = useState(false);
//   const [docTypesError, setDocTypesError] = useState(null);

//   // State for mapping document type GUIDs to names in the table
//   const [docTypeNameMap, setDocTypeNameMap] = useState({});

//   // Fetch document types when component mounts OR when orgId changes
//   useEffect(() => {
//     const loadDocumentTypes = async () => {
//       if (!orgId) {
//         console.log("No orgId provided, skipping document types fetch");
//         return;
//       }

//       setDocTypesLoading(true);
//       setDocTypesError(null);
//       try {
//         const types = await fetchDocumentTypesByOrganisation(orgId);
//         console.log("Fetched document types:", types);
//         setDocumentTypes(types || []);

//         // Create a map for GUID to Name
//         const map = {};
//         (types || []).forEach((type) => {
//           if (type.docTypeGuid && type.docTypeName) {
//             map[type.docTypeGuid.toLowerCase()] = type.docTypeName;
//           }
//         });
//         setDocTypeNameMap(map);
//         console.log("Document type name map:", map);
//       } catch (error) {
//         console.error("Error loading document types:", error);
//         setDocTypesError("Failed to load document types");
//         setDocumentTypes([]);
//       } finally {
//         setDocTypesLoading(false);
//       }
//     };

//     // Load document types on component mount and when orgId changes
//     loadDocumentTypes();
//   }, [orgId]); // Removed showUploadModal dependency

//   // Function to get document type name from GUID
//   const getDocumentTypeName = (docTypeGuid) => {
//     if (!docTypeGuid) return "General";

//     // Check if it's a GUID format (UUID)
//     const isGuid =
//       /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
//         docTypeGuid,
//       );

//     if (isGuid) {
//       const lowerGuid = docTypeGuid.toLowerCase();
//       const mappedName = docTypeNameMap[lowerGuid];
//       if (mappedName) {
//         return mappedName;
//       }
//       // If not found in map, return truncated GUID
//       return docTypeGuid.substring(0, 8);
//     }
//     // If it's not a GUID (legacy data), return as is
//     return docTypeGuid;
//   };

//   return (
//     <div className="pdt-documents-container pdt-fade-in">
//       <div className="pdt-document-card">
//         <div className="pdt-header">
//           <div className="pdt-title-section">
//             <h3 className="pdt-main-title">
//               <i className="fas fa-folder-open me-2"></i>
//               Project Documents
//             </h3>
//             <p className="pdt-subtitle">
//               Manage all documents related to this project
//             </p>
//           </div>
//           <Button
//             variant="primary"
//             onClick={() => setShowUploadModal(true)}
//             className="pdt-btn-upload"
//           >
//             <i className="fas fa-upload"></i>
//             Upload Documents
//           </Button>
//         </div>

//         {documentsLoading || docTypesLoading ? (
//           <div className="pdt-loading-container">
//             <div className="pdt-spinner"></div>
//             <p className="pdt-loading-text">
//               {documentsLoading
//                 ? "Loading documents..."
//                 : "Loading document types..."}
//             </p>
//           </div>
//         ) : documents.length === 0 ? (
//           <div className="pdt-empty-container">
//             <div className="pdt-empty-icon">
//               <i className="fas fa-folder-open"></i>
//             </div>
//             <h5 className="pdt-empty-title">No Documents Found</h5>
//             <p className="pdt-empty-message">
//               No documents have been uploaded for this project yet.
//             </p>
//             <Button
//               variant="outline-primary"
//               onClick={() => setShowUploadModal(true)}
//               className="pdt-btn-outline-upload"
//             >
//               <i className="fas fa-plus"></i>
//               Upload Your First Document
//             </Button>
//           </div>
//         ) : (
//           <div className="pdt-table-wrapper">
//             <table className="pdt-document-table">
//               <thead className="pdt-table-header">
//                 <tr>
//                   <th className="pdt-col-name">Document Name</th>
//                   <th className="pdt-col-type">Type</th>
//                   <th className="pdt-col-version">Version</th>
//                   <th className="pdt-col-uploadedby">Uploaded By</th>
//                   <th className="pdt-col-date">Upload Date</th>
//                   <th className="pdt-col-actions">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {documents.map((doc, index) => (
//                   <tr key={doc.documentId || index} className="pdt-table-row">
//                     <td className="pdt-col-name">
//                       <div className="pdt-document-name">
//                         <div className="pdt-document-icon">
//                           <HiOutlineDocumentText />
//                         </div>
//                         <div className="pdt-document-title">
//                           {doc.documentTitle}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="pdt-col-type">
//                       <Badge bg="secondary" className="pdt-type-badge">
//                         {getDocumentTypeName(doc.documentType)}
//                       </Badge>
//                     </td>
//                     <td className="pdt-col-version">
//                       <div className="pdt-version-badge">
//                         {doc.documentVersion || 1}
//                       </div>
//                     </td>
//                     <td className="pdt-col-uploadedby">
//                       <div className="pdt-uploadedby-info">
//                         <div className="pdt-user-avatar">
//                           {(doc.createdBy || "U").charAt(0).toUpperCase()}
//                         </div>
//                         <div className="pdt-uploadedby-name">
//                           {doc.createdBy || "Unknown"}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="pdt-col-date">
//                       <div className="pdt-date-info">
//                         <div className="pdt-date-main">
//                           {doc.createdOnDate
//                             ? moment(doc.createdOnDate).format("MMM DD, YYYY")
//                             : "-"}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="pdt-col-actions">
//                       <div className="pdt-actions">
//                         <Button
//                           variant="outline-primary"
//                           size="sm"
//                           onClick={() => handleDownloadDocument(doc)}
//                           className="pdt-btn-download"
//                           title="Download Document"
//                         >
//                           Download
//                         </Button>
//                         <Button
//                           variant="outline-danger"
//                           size="sm"
//                           onClick={() =>
//                             handleDeleteDocument(
//                               doc.documentId,
//                               doc.documentTitle,
//                             )
//                           }
//                           className="pdt-btn-delete"
//                           title="Delete Document"
//                         >
//                           Delete
//                         </Button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>

//       {/* Upload Document Modal */}
//       <Modal
//         show={showUploadModal}
//         onHide={() => {
//           setShowUploadModal(false);
//           // Reset form when modal closes
//           setDocumentTitle("");
//           setDocumentType("");
//         }}
//         size="lg"
//         centered
//         className="pdt-modal"
//       >
//         <Modal.Header closeButton className="pdt-modal-header">
//           <Modal.Title className="pdt-modal-title">
//             <i className="fas fa-cloud-upload-alt me-2"></i>
//             Upload Documents
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="pdt-modal-body">
//           <Form>
//             <div className="pdt-form-group">
//               <Form.Label className="pdt-form-label">
//                 <i className="fas fa-heading me-1"></i>
//                 Document Title *
//               </Form.Label>
//               <Form.Control
//                 type="text"
//                 placeholder="Enter document title"
//                 value={documentTitle}
//                 onChange={(e) => setDocumentTitle(e.target.value)}
//                 className="pdt-form-control"
//                 required
//               />
//             </div>

//             <div className="pdt-form-group">
//               <Form.Label className="pdt-form-label">
//                 <i className="fas fa-tag me-1"></i>
//                 Document Type *
//               </Form.Label>
//               {docTypesLoading ? (
//                 <div className="pdt-loading-types">
//                   <Spinner size="sm" animation="border" />
//                   <span className="ms-2">Loading document types...</span>
//                 </div>
//               ) : docTypesError ? (
//                 <div className="pdt-error-message">
//                   <i className="fas fa-exclamation-triangle me-1"></i>
//                   {docTypesError}
//                 </div>
//               ) : documentTypes.length > 0 ? (
//                 <Form.Select
//                   value={documentType}
//                   onChange={(e) => setDocumentType(e.target.value)}
//                   className="pdt-form-select"
//                   required
//                 >
//                   <option value="">-- Select Document Type --</option>
//                   {documentTypes.map((type) => (
//                     <option key={type.id} value={type.docTypeGuid}>
//                       {type.docTypeName}
//                     </option>
//                   ))}
//                 </Form.Select>
//               ) : (
//                 <Form.Control
//                   type="text"
//                   placeholder="Enter document type manually (no types found in database)"
//                   value={documentType}
//                   onChange={(e) => setDocumentType(e.target.value)}
//                   className="pdt-form-control"
//                 />
//               )}
//               {documentTypes.length === 0 &&
//                 !docTypesLoading &&
//                 !docTypesError && (
//                   <Form.Text className="pdt-form-text text-warning">
//                     <i className="fas fa-info-circle me-1"></i>
//                     No document types found. You can enter a type manually or
//                     add types in the Document Type Manager.
//                   </Form.Text>
//                 )}
//             </div>

//             <div className="pdt-form-group">
//               <Form.Label className="pdt-form-label">
//                 <i className="fas fa-paperclip me-1"></i>
//                 Select Files *
//               </Form.Label>
//               <Form.Control
//                 type="file"
//                 multiple
//                 onChange={handleFileSelect}
//                 accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
//                 className="pdt-file-input"
//                 required
//               />
//               <Form.Text className="pdt-form-text">
//                 <i className="fas fa-info-circle me-1"></i>
//                 Maximum file size: 25MB per file. Supported formats: PDF, Word,
//                 Excel, PowerPoint, Images, Text, Archives
//               </Form.Text>
//             </div>

//             {selectedFiles.length > 0 && (
//               <div className="pdt-selected-files">
//                 <div className="pdt-selected-files-title">
//                   Selected Files ({selectedFiles.length})
//                 </div>
//                 <ul className="pdt-file-list">
//                   {selectedFiles.map((file, index) => (
//                     <li key={index} className="pdt-file-item">
//                       <div className="pdt-file-info">
//                         <span className="pdt-file-name">{file.name}</span>
//                       </div>
//                       <div className="pdt-file-size">
//                         {(file.size / (1024 * 1024)).toFixed(2)} MB
//                       </div>
//                     </li>
//                   ))}
//                 </ul>
//               </div>
//             )}
//           </Form>
//         </Modal.Body>
//         <Modal.Footer className="pdt-modal-footer">
//           <Button
//             variant="secondary"
//             onClick={() => {
//               setShowUploadModal(false);
//               setDocumentTitle("");
//               setDocumentType("");
//             }}
//             className="pdt-btn-cancel"
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleUploadDocuments}
//             disabled={
//               selectedFiles.length === 0 ||
//               uploadingDocuments ||
//               !documentTitle.trim() ||
//               !documentType
//             }
//             className="pdt-btn-submit"
//           >
//             {uploadingDocuments ? (
//               <>
//                 <div className="pdt-btn-spinner"></div>
//                 Uploading...
//               </>
//             ) : (
//               <>
//                 Upload {selectedFiles.length} File
//                 {selectedFiles.length !== 1 ? "s" : ""}
//               </>
//             )}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </div>
//   );
// };

// export default ProjectDocumentsTab;
